// SHARED — keep in sync across pro/care/backbar/academy (canonical: workspace/shared-web-auth/). Extract to @rootegra/auth later.
//
// createRootegraClient — one Supabase client factory for every Rootegra web app.
//
// On *.rootegra.com the session is stored in a cookie on `.rootegra.com`, so a
// sign-in on any app (pro / care / academy / backbar) is visible to all of
// them — that is what makes the cross-app experience switcher work. On any
// other hostname (localhost dev, preview URLs) it falls back to plain
// supabase-js with localStorage, so local development is unchanged.
//
// ⚠️ SECURITY — auth blast radius of the `.rootegra.com` cookie:
// Because the session cookie is scoped to `.rootegra.com`, it is readable by
// EVERY subdomain of rootegra.com — not just the four apps. That includes
// review.rootegra.com (and its Pages Functions, which see the cookie on every
// request) and ANY subdomain added in the future. Standing up a new
// *.rootegra.com host (marketing page, preview, third-party CNAME, status
// page, …) silently joins it to the auth blast radius: script or server code
// on that host can lift the user's Supabase session. Before adding a
// subdomain, either accept that it is auth-trusted or put it on a different
// apex domain.

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient, SupabaseClientOptions } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
import { preserveLegacyPkceVerifiers } from './sessionMigration';

export type RootegraClientOptions = {
  /** Per-app auth options (flowType, detectSessionInUrl, …). See caveats below. */
  auth?: SupabaseClientOptions<'public'>['auth'];
  /** Per-app global options — used for the active-context fetch wrapper (global.fetch). */
  global?: SupabaseClientOptions<'public'>['global'];
};

// supabase-js v2 coordinates token refresh across tabs with the Web Locks
// API (navigator.locks). Under React StrictMode's mount→unmount→remount,
// an interrupted lock holder can leave `lock:sb-<ref>-auth-token` held
// forever; every later `.from()` call invokes getSession() → waits on
// that lock → deadlocks (no REST request is ever sent and the UI hangs).
// These are single-tab SPAs, so a pass-through lock is safe and removes
// the entire deadlock class. (Carried over from each app's original
// src/lib/supabase.ts — keep it.)
const passThroughLock = async <T>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<T>,
): Promise<T> => fn();

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/**
 * True when this page is served from the shared production domain.
 *
 * Exact match on the apex or a real `.rootegra.com` subdomain — a bare
 * endsWith('rootegra.com') would also match unrelated hosts like
 * `evilrootegra.com`, and this predicate gates whether we write the shared
 * auth cookie, so it must not be spoofable by a lookalike domain.
 */
export function isRootegraDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'rootegra.com' || hostname.endsWith('.rootegra.com');
}

/**
 * Create the app's Supabase client.
 *
 * Caveats on the cookie branch (verified against @supabase/ssr 0.10.3 source):
 * - `flowType` is ALWAYS forced to 'pkce' by createBrowserClient; a per-app
 *   flowType option only takes effect on the localStorage (dev) branch.
 *   Practical consequence: on *.rootegra.com, magic links / OTP links must be
 *   opened in the same browser that requested them (care already lives with
 *   this; it is the normal Supabase PKCE trade-off).
 * - `detectSessionInUrl`, `autoRefreshToken`, `persistSession` and `lock` DO
 *   pass through from opts.auth.
 * - Session storage itself is always the `.rootegra.com` cookie; a custom
 *   auth.storage option would be ignored there.
 */
export function createRootegraClient(opts?: RootegraClientOptions): SupabaseClient {
  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  if (!url || !anon) {
    console.warn('[rootegra] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
  }

  const auth = {
    persistSession: true,
    autoRefreshToken: true,
    lock: passThroughLock,
    ...opts?.auth,
  };

  if (isRootegraDomain()) {
    // Storage-cutover bridge: an in-flight PKCE code verifier written to
    // localStorage by the previous (pre-cookie) build must be visible to the
    // cookie storage BEFORE the client exists, or a magic link requested
    // pre-deploy and opened post-deploy fails its ?code= exchange. Synchronous,
    // never throws, keeps the localStorage copy. See sessionMigration.ts.
    preserveLegacyPkceVerifiers();
    return createBrowserClient(url, anon, {
      global: opts?.global,
      auth,
      cookieOptions: {
        domain: '.rootegra.com',
        path: '/',
        sameSite: 'lax',
        secure: true,
        maxAge: ONE_YEAR_SECONDS,
      },
      // Each app builds exactly one client itself; the module-level singleton
      // cache inside @supabase/ssr would silently ignore differing options on
      // a second call, so opt out and keep factory semantics predictable.
      isSingleton: false,
    });
  }

  // localhost / preview: plain supabase-js, localStorage session — unchanged dev flow.
  return createClient(url, anon, {
    global: opts?.global,
    auth,
  });
}
